import React, { useState, useMemo, useEffect } from 'react';
import { getStoryPillars, getLeadershipProfile } from '../data/skillsStory.index';
import { useLanguage } from '../context/LanguageContext';

function Skills() {
  const { lang, t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const pillars = useMemo(() => getStoryPillars(lang), [lang]);
  const leadership = useMemo(() => getLeadershipProfile(lang), [lang]);

  useEffect(() => {
    setActiveFilter('all');
    setSearchTerm('');
  }, [lang]);

  const filteredPillars = useMemo(() => {
    return pillars.filter((pillar) => {
      const matchesFilter = activeFilter === 'all' || pillar.id === activeFilter;
      if (!matchesFilter) return false;

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();

      const inCategory = pillar.category.toLowerCase().includes(term);
      const inHeadline = pillar.headline.toLowerCase().includes(term);
      const inProblem = pillar.problem.toLowerCase().includes(term);
      const inApproach = pillar.approach.toLowerCase().includes(term);
      const inImpact = pillar.impact.toLowerCase().includes(term);
      const inStack = pillar.stack.some(
        (s) => s.name.toLowerCase().includes(term) || s.role.toLowerCase().includes(term)
      );

      return inCategory || inHeadline || inProblem || inApproach || inImpact || inStack;
    });
  }, [pillars, activeFilter, searchTerm]);

  return (
    <div className="space-y-16 animate-fade-in text-white max-w-6xl mx-auto">
      {/* Page Title & Search Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/10">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/25 text-primary-300 text-xs font-semibold tracking-wide">
            <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
            {leadership.role}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            {t('sections.skillsTitle')}
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-3xl leading-relaxed font-normal">
            {t('sections.skillsStorySubtitle')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-80 group">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('nav.searchPlaceholder')}
            aria-label={t('nav.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all text-sm"
          />
          <svg
            className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary-400 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40 hover:text-white transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Executive Leadership & Compliance Posture Spotlight (Wide 2-Column Grid) */}
      <section className="relative p-7 sm:p-10 md:p-12 rounded-[2.5rem] bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.01] border border-white/10 shadow-2xl space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-white/10 pb-8">
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
              {t('sections.executiveBannerTitle')}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              {leadership.role}
            </h2>
            <p className="text-sm sm:text-base font-semibold text-primary-300">
              {leadership.organization}
            </p>
          </div>
          <p className="text-base text-white/85 max-w-2xl leading-relaxed font-normal">
            {leadership.summary}
          </p>
        </div>

        {/* 2 Wide Columns for Credentials (Easy to read, never squished) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {leadership.highlights.map((item, idx) => (
            <div
              key={idx}
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.06] transition-all space-y-3 group shadow-md"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-block px-3 py-1 rounded-lg bg-primary-500/15 border border-primary-500/25 text-xs font-bold text-primary-300">
                  {item.badge}
                </span>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
              </div>
              <h3 className="text-base font-bold text-white group-hover:text-primary-300 transition-colors">
                {item.title}
              </h3>
              <p className="text-sm text-white/75 leading-relaxed font-normal">
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          onClick={() => setActiveFilter('all')}
          className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
            activeFilter === 'all'
              ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25 border border-primary-400'
              : 'bg-white/[0.03] border border-white/10 text-white/75 hover:text-white hover:bg-white/[0.07]'
          }`}
        >
          {t('sections.filterAll') || t('sections.all')}
        </button>
        {pillars.map((pillar) => (
          <button
            key={pillar.id}
            onClick={() => setActiveFilter(pillar.id)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
              activeFilter === pillar.id
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25 border border-primary-400'
                : 'bg-white/[0.03] border border-white/10 text-white/75 hover:text-white hover:bg-white/[0.07]'
            }`}
          >
            {pillar.category}
          </button>
        ))}
      </div>

      {/* Story Cards List: Spacious, Sequential Full-Width Story Flow */}
      <div className="space-y-16">
        {filteredPillars.length > 0 ? (
          filteredPillars.map((pillar, pIdx) => (
            <article
              key={pillar.id}
              className="p-8 sm:p-10 md:p-14 rounded-[2.5rem] bg-gradient-to-b from-white/[0.04] to-white/[0.015] border border-white/10 hover:border-white/20 transition-all duration-300 space-y-10 group shadow-2xl"
            >
              {/* Pillar Header */}
              <div className="space-y-3 border-b border-white/10 pb-8">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-primary-500/15 border border-primary-500/30 text-primary-300 flex items-center justify-center font-extrabold text-base shadow-sm">
                    0{pIdx + 1}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
                    {pillar.category}
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight">
                  {pillar.headline}
                </h2>
              </div>

              {/* Spacious Sequential Narrative (Never Narrow / Squeezed) */}
              <div className="space-y-6">
                {/* 1. Problem / Challenge (Full-Width Card) */}
                <div className="p-7 sm:p-8 rounded-2xl bg-amber-500/[0.04] border border-amber-500/20 space-y-3 hover:border-amber-500/35 transition-colors">
                  <div className="flex items-center gap-2.5 text-amber-300">
                    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-xs font-extrabold uppercase tracking-wider">
                      {t('sections.problemTitle')}
                    </span>
                  </div>
                  <p className="text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                    {pillar.problem}
                  </p>
                </div>

                {/* 2. Architectural Approach (Full-Width Card) */}
                <div className="p-7 sm:p-8 rounded-2xl bg-sky-500/[0.04] border border-sky-500/20 space-y-3 hover:border-sky-500/35 transition-colors">
                  <div className="flex items-center gap-2.5 text-sky-300">
                    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="text-xs font-extrabold uppercase tracking-wider">
                      {t('sections.approachTitle')}
                    </span>
                  </div>
                  <p className="text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                    {pillar.approach}
                  </p>
                </div>

                {/* 3. Measured Impact (Full-Width Card) */}
                <div className="p-7 sm:p-8 rounded-2xl bg-emerald-500/[0.04] border border-emerald-500/20 space-y-3 hover:border-emerald-500/35 transition-colors">
                  <div className="flex items-center gap-2.5 text-emerald-300">
                    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs font-extrabold uppercase tracking-wider">
                      {t('sections.impactTitle')}
                    </span>
                  </div>
                  <p className="text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                    {pillar.impact}
                  </p>
                </div>
              </div>

              {/* Contextual Stack in Practice (Spacious 2-Column Grid) */}
              <div className="pt-8 border-t border-white/10 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white/70">
                    {t('sections.stackTitle')}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pillar.stack.map((item, sIdx) => (
                    <div
                      key={sIdx}
                      className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary-500/50 hover:bg-white/[0.06] transition-all group/badge flex items-start gap-3.5 shadow-sm"
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-400 mt-2 shrink-0"></span>
                      <div className="space-y-1">
                        <div className="font-bold text-base text-white group-hover/badge:text-primary-300 transition-colors">
                          {item.name}
                        </div>
                        <p className="text-sm text-white/75 leading-relaxed font-normal">
                          {item.role}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="py-20 text-center space-y-4 rounded-3xl bg-white/[0.02] border border-white/10">
            <div className="text-4xl text-white/30 italic">:(</div>
            <h3 className="text-xl text-white font-medium">{t('nav.noSkills')}</h3>
            <p className="text-white/60 text-sm">{t('nav.tryAdjust')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Skills;
